import React, { useEffect } from "react";

interface BaseModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title: string;
  lastUpdated?: string;
  children: React.ReactNode;
  footerButtonText?: string;
  onFooterButtonClick?: () => void;
  allowBackdropClose?: boolean;
  showCloseButton?: boolean;
  maxWidth?: string;
  maxHeight?: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  lastUpdated = "October 2025",
  children,
  footerButtonText,
  onFooterButtonClick,
  allowBackdropClose = true,
  showCloseButton = true,
  maxWidth = "max-w-2xl",
  maxHeight = "max-h-[85vh]",
}) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && allowBackdropClose && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, allowBackdropClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && allowBackdropClose && onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      style={{ minHeight: "100vh" }}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl ${maxWidth} w-full ${maxHeight} flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-col">
            <h2 className="text-2xl font-semibold text-[#46ACC6]">{title}</h2>
            {lastUpdated && (
              <span className="text-xs text-gray-400 mt-1">
                Last updated: {lastUpdated}
              </span>
            )}
          </div>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Content */}
          <div className="p-6">{children}</div>

          {/* Footer */}
          {footerButtonText && onFooterButtonClick && (
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={onFooterButtonClick}
                className="px-6 py-2 bg-[#46ACC6] text-white rounded-lg hover:bg-[#3a9bb3] transition-colors font-medium"
              >
                {footerButtonText}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseModal;
